import { DownloadOutlined, LoadingOutlined, FileTextOutlined, GithubOutlined } from '@ant-design/icons';
import { useMemo } from 'react';
import { UAParser } from 'ua-parser-js';
import prettyBytes from 'pretty-bytes';
import cs from 'classnames';
import Styles from '../../Styles.module.scss';
import { useGetLatestReleaseQuery, ReleaseAsset } from '../../store/DistService';

type Platform = 'darwin-arm64' | 'darwin-amd64' | 'linux-amd64' | 'linux-arm64' | 'windows-amd64';

interface ParsedAsset extends ReleaseAsset {
    platform: Platform | null;
    displayName: string;
    isRecommended: boolean;
}

function detectUserPlatform(): Platform | null {
    const parser = new UAParser();
    const osName = parser.getOS().name;
    const cpuArch = parser.getCPU().architecture;

    if (!osName) return null;

    if (osName.includes('Mac')) {
        return cpuArch === 'amd64' ? 'darwin-amd64' : 'darwin-arm64';
    }
    if (osName.includes('Windows')) {
        return 'windows-amd64';
    }
    if (osName.includes('Linux')) {
        return cpuArch === 'arm64' ? 'linux-arm64' : 'linux-amd64';
    }

    return null;
}

function parsePlatform(filename: string): Platform | null {
    if (filename.includes('darwin-arm64')) return 'darwin-arm64';
    if (filename.includes('darwin-amd64')) return 'darwin-amd64';
    if (filename.includes('linux-amd64') || filename.includes('_amd64')) return 'linux-amd64';
    if (filename.includes('linux-arm64') || filename.includes('_arm64')) return 'linux-arm64';
    if (filename.includes('windows-amd64')) return 'windows-amd64';
    return null;
}

function getDisplayName(platform: Platform | null, filename: string): string {
    if (filename.endsWith('.deb')) {
        const arch = platform === 'linux-arm64' ? 'ARM64' : 'x86_64';
        return `Linux .deb (${arch})`;
    }
    if (filename.endsWith('.rpm')) {
        const arch = platform === 'linux-arm64' ? 'ARM64' : 'x86_64';
        return `Linux .rpm (${arch})`;
    }
    switch (platform) {
        case 'darwin-arm64':
            return 'macOS (Apple Silicon)';
        case 'darwin-amd64':
            return 'macOS (Intel)';
        case 'linux-amd64':
            return 'Linux (x86_64)';
        case 'linux-arm64':
            return 'Linux (ARM64)';
        case 'windows-amd64':
            return 'Windows (x64)';
        default:
            return 'Unknown';
    }
}

function formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
}

export function Downloads() {
    const { data: release, isLoading, error } = useGetLatestReleaseQuery();
    const userPlatform = useMemo(() => detectUserPlatform(), []);

    const parsedAssets = useMemo<ParsedAsset[]>(() => {
        if (!release?.assets) return [];

        return release.assets
            .filter(asset => {
                const name = asset.name.toLowerCase();
                return (
                    name.startsWith('modelpark') &&
                    (name.endsWith('.tar.gz') || name.endsWith('.zip') ||
                     name.endsWith('.deb') || name.endsWith('.rpm'))
                );
            })
            .map(asset => {
                const platform = parsePlatform(asset.name);
                return {
                    ...asset,
                    platform,
                    displayName: getDisplayName(platform, asset.name),
                    isRecommended: platform === userPlatform,
                };
            })
            .sort((a, b) => {
                if (a.isRecommended && !b.isRecommended) return -1;
                if (!a.isRecommended && b.isRecommended) return 1;
                return a.displayName.localeCompare(b.displayName);
            });
    }, [release, userPlatform]);

    const checksumsAsset = release?.assets.find(a => a.name === 'checksums.txt');

    if (isLoading) {
        return (
            <div className={Styles.Section}>
                <h2 className={Styles.SectionTitle}>Downloads</h2>
                <div className={Styles.Loading}>
                    <LoadingOutlined style={{ fontSize: 24, marginRight: 12 }} />
                    Loading releases...
                </div>
            </div>
        );
    }

    if (error || !release) {
        return (
            <div className={Styles.Section}>
                <h2 className={Styles.SectionTitle}>Downloads</h2>
                <p className={Styles.SectionDescription}>
                    Unable to load releases. Please try again later or contact support.
                </p>
            </div>
        );
    }

    const version = release.version || release.tag_name;

    return (
        <div className={Styles.Section}>
            <h2 className={Styles.SectionTitle}>Downloads</h2>

            <div className={Styles.ReleaseHeader}>
                <div>
                    <span className={Styles.ReleaseVersion}>{version}</span>
                    <span className={Styles.ReleaseDate} style={{ marginLeft: 16 }}>
                        Released {formatDate(release.published_at)}
                    </span>
                </div>
                <a
                    href={release.html_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={Styles.ReleaseLink}
                >
                    <GithubOutlined style={{ marginRight: 6 }} />
                    Release Notes
                </a>
            </div>

            <div className={Styles.DownloadsGrid}>
                {parsedAssets.map(asset => (
                    <a
                        key={asset.name}
                        href={asset.browser_download_url}
                        className={cs(Styles.DownloadCard, {
                            [Styles.recommended]: asset.isRecommended,
                        })}
                    >
                        <div className={Styles.DownloadInfo}>
                            <div className={Styles.DownloadName}>
                                {asset.displayName}
                                {asset.isRecommended && (
                                    <span className={Styles.RecommendedBadge}>Recommended</span>
                                )}
                            </div>
                            <div className={Styles.DownloadMeta}>
                                {prettyBytes(asset.size)}
                                {asset.download_count !== undefined &&
                                    ` · ${asset.download_count.toLocaleString()} downloads`}
                            </div>
                        </div>
                        <DownloadOutlined className={Styles.DownloadIcon} />
                    </a>
                ))}
            </div>

            {checksumsAsset && (
                <a
                    href={checksumsAsset.browser_download_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={Styles.ManualDownloadLink}
                >
                    <FileTextOutlined /> View checksums
                </a>
            )}
        </div>
    );
}
